/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { Button, DialogActions, DialogContent, Grid } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { omit } from "lodash";
import { Modal } from "components/haven-design";
import { CreateBuilding, PropertyBuildings, UpdateBuilding } from "types/gql-types";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import BuildingForm, { BuildingType } from "./building-form";
import BuildingItem from "./building-item";
import { CREATE_BUILDING, DELETE_BUILDING, QUERY_PROPERTY_BUILDINGS, UPDATE_BUILDING } from "./query";


type Props = {
  propertyId: string;
  isAdmin?: boolean;
  isSupervisor?: boolean;
}

const BuildingList: React.FC<Props> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();
  const { loading, data, error } = useQuery<PropertyBuildings>(QUERY_PROPERTY_BUILDINGS, {
    variables: { propertyId },
    fetchPolicy: "network-only",
  });
  const [open, setOpen] = useState(false);
  const [createBuildingMutation, { loading: createLoading }] = useMutation<CreateBuilding>(CREATE_BUILDING, { refetchQueries: ["PropertyBuildings"] });
  const [updateBuildingMutation, { loading: updateLoading }] = useMutation<UpdateBuilding>(UPDATE_BUILDING, { refetchQueries: ["PropertyBuildings"] });
  const [deleteBuildingMutation, { loading: deleteLoading }] = useMutation<UpdateBuilding>(DELETE_BUILDING, { refetchQueries: ["PropertyBuildings"] });

  const createBuilding = (input: BuildingType) =>
    createBuildingMutation({ variables: { propertyId, input } })
      .then(() => openSnackbar({ message: "New Building Successfully Added", status: "success" }));
  const deleteBuilding = (id: string) =>
    deleteBuildingMutation({ variables: { propertyId, id } })
      .then(() => openSnackbar({ message: "Building Successfully Removed", status: "success" }));
  const updateBuilding = (input: BuildingType) =>
    updateBuildingMutation({ variables: {
      propertyId,
      input: omit(input, ["id", "__typename"]),
      id: input.id,
    } })
      .then(() => openSnackbar({ message: "Building Successfully Updated", status: "success" }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!data) return <ServerError desc="Missing Data" />;
  const { propertyBuildings: buildings } = data;

  return (
    <Grid container spacing={2}>
      <Grid container size={{ xs: 12 }} justifyContent="flex-end">
        <Button
          size="large"
          color="primary"
          variant="contained"
          onClick={handleOpen}
          disabled={createLoading || updateLoading || deleteLoading}
        >
            Add Building
        </Button>
      </Grid>
      {buildings.map((building) => (
        <Grid key={building.id} size={{ xs: 12 }}>
          <BuildingItem
            {...building}
            updateBuilding={updateBuilding}
            deleteBuilding={deleteBuilding}
          />
        </Grid>
      ))}
      <Modal isOpen={open} onRequestClose={handleClose} title="Add Building" fullScreen>
        <DialogContent>
          <BuildingForm
            id="buliding-form"
            onSubmit={(formData) => {
              createBuilding({ ...formData });
              handleClose();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="large" color="secondary" variant="outlined" onClick={handleClose}>
              Cancel
          </Button>
          <Button size="large" color="primary" variant="contained" type="submit" form="buliding-form">
              Add Building
          </Button>
        </DialogActions>
      </Modal>
    </Grid>
  );
};

export default BuildingList;
